import React, { AllHTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import Layout from "components/Layout";
import dayjs from 'dayjs';
import XtLoading from "@/components/Xt/Loading";
import styles from "./index.module.less";
import { getUserLetterGroup, getUserLetterList, getUserLetterRead, getUserLetterSingleRead } from "@/api/xt-message";
import { useRouter } from "next/router";
import Nodata from "assets/images/home/nodata.png";

// 与 Nav 一致：进入 notification 页时再更新“已读”时间，这样 nav 小红点会在进入页面后清除
const AUTO_SPOT_NOTIFY_LOCAL_KEY = "autoSpotNotifyLocal";
function getStoredNotifyTime(): number | null {
  try {
    const raw = localStorage.getItem(AUTO_SPOT_NOTIFY_LOCAL_KEY);
    if (!raw) return null;
    const { time } = JSON.parse(raw);
    return typeof time === "number" ? time : null;
  } catch {
    return null;
  }
}

// notification 页单独 storage：按四个 tab 的 type 分别存储是否已浏览，浏览过的 tab 下次进页不再展示小红点
const NOTIFICATION_TAB_READ_KEY = "notificationTabRead";
function getTabReadStorage(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(NOTIFICATION_TAB_READ_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}
function setTabViewedInStorage(type: number) {
  try {
    const prev = getTabReadStorage();
    const next = { ...prev, [String(type)]: true };
    localStorage.setItem(NOTIFICATION_TAB_READ_KEY, JSON.stringify(next));
  } catch (e) {}
}

import { message } from 'antd'
const { getUrl } = Util;
const { useDocumentWidth } = Hooks;
interface NotificationProps extends AllHTMLAttributes<HTMLElement> {
  attr?: any;
}
interface GroupItem {
  type: number;
  iconUrl: string;
  name: string;
  unreadNum: number
}
const { useTranslation } = Hooks;
const NotificationCenter: React.FC<NotificationProps> = ({ className }) => {
  const t = useTranslation();
  const router = useRouter();
  const domeWidth = (useDocumentWidth() ?? 0) > 768;
  const isMobile = !domeWidth;
  // 默认选中 Important Notice（重要公告）tab，对应 type 4
  const [navActive, setNavActive] = useState(4);
  const [loading, setLoading] = useState(false);
  const [groupData, setGroupData] = useState<GroupItem[]>([{
    type: 1,
    unreadNum: 0,
    iconUrl: '',
    name: ''
  }, {
    type: 2,
    unreadNum: 0,
    iconUrl: '',
    name: ''
  }, {
    type: 3,
    unreadNum: 0,
    iconUrl: '',
    name: ''
  }, {
    type: 4,
    unreadNum: 0,
    iconUrl: '',
    name: ''
  }]);
  const [notificationData, setNotificationData] = useState<any[]>([]);
  const [isMore, setIsMore] = useState(false);
  // 各 tab 是否已浏览（从 notification 页单独 storage 读取），用于控制小红点：仅未浏览且接口有未读数时展示
  const [tabReadMap, setTabReadMap] = useState<Record<string, boolean>>(() => getTabReadStorage());

  useEffect(() => {
    getGroupData(false);
  }, []);

  // 进入 notification 页时：若此前已有“已读”时间则更新以清除 nav 小红点；并标记当前选中的 tab 为已浏览
  useEffect(() => {
    if (getStoredNotifyTime() != null) {
      try {
        localStorage.setItem(AUTO_SPOT_NOTIFY_LOCAL_KEY, JSON.stringify({ value: 1, time: Date.now() }));
      } catch (e) {}
    }
    setTabViewedInStorage(navActive);
    setTabReadMap((prev) => ({ ...prev, [String(navActive)]: true }));
  }, []);

  // 从 URL 同步 type 后，也标记该 tab 为已浏览
  useEffect(() => {
    if (router.query.type) {
      const type = +router.query.type;
      setTabViewedInStorage(type);
      setTabReadMap((prev) => ({ ...prev, [String(type)]: true }));
    }
  }, [router.query.type]);

  useEffect(() => {
    getListData();
  }, [navActive]);

  useEffect(() => {
    if (router.query.type) {
      setNavActive(+router.query.type)
    }
  }, [router.query]);


  const getGroupData = async (flag = true) => {
    const res = await getUserLetterGroup({});
    if (flag) {
      setNavActive(res[0].type);
    }
    setGroupData(res);
  };

  const getListData = async (since = '') => {
    setLoading(true);
    const res = await getUserLetterList({
      params: {
        type: navActive,
        pageSize: 10,
        since
      }
    })
    let newData = res.list;
    const ids = newData.filter(item => !item.isRead && !item.webClickUrl).map(item => item.id);
    if (ids.length) {
      singleRead({}, ids.join(','));
      newData = res.list.map(item => {
        if (ids.includes(item.id)) {
          item.isRead = true;
        }
        return item;
      })
    }
    setIsMore(newData.length == 10)
    setNotificationData(since ? [...notificationData, ...newData] : newData);
    setLoading(false);
  }
  // Tab 顺序：1. Important Notice  2. Hot Campaign  3. New Listing  4. System Message（对应 groupData[3][2][1][0]）
  const navData = useMemo(() =>
    [
      {
        type: groupData[3].type,
        name: t('user.announcement'),
        icon: <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.5086 14.4044H16.2765V8.24397C16.2765 4.7245 13.5573 1.81091 10.1161 1.49702V0.851418C10.1161 0.512569 9.83884 0.235352 9.50001 0.235352C9.16118 0.235352 8.88396 0.512569 8.88396 0.8514V1.49702C5.44274 1.81089 2.72351 4.72446 2.72351 8.24395V14.4044H1.49141C1.32803 14.4044 1.17133 14.4693 1.0558 14.5848C0.940271 14.7003 0.875366 14.857 0.875366 15.0204C0.875366 15.1838 0.940271 15.3405 1.0558 15.456C1.17133 15.5716 1.32803 15.6365 1.49141 15.6365H17.5086C17.672 15.6365 17.8287 15.5716 17.9442 15.456C18.0598 15.3405 18.1247 15.1838 18.1247 15.0204C18.1247 14.857 18.0598 14.7003 17.9442 14.5848C17.8287 14.4693 17.672 14.4044 17.5086 14.4044ZM9.50001 18.7648C9.74398 18.7648 9.98556 18.7168 10.211 18.6234C10.4364 18.5301 10.6412 18.3933 10.8137 18.2208C10.9862 18.0482 11.123 17.8434 11.2164 17.618C11.3097 17.3926 11.3577 17.151 11.3577 16.9071H7.64233C7.64229 17.151 7.69031 17.3926 7.78366 17.618C7.877 17.8434 8.01383 18.0482 8.18634 18.2208C8.35885 18.3933 8.56366 18.5301 8.78906 18.6234C9.01446 18.7168 9.25605 18.7648 9.50001 18.7648Z" fill="currentColor" />
        </svg>,
        num: groupData[3].unreadNum
      },
      {
        type: groupData[2].type,
        name: t('user.activity'),
        icon: <svg width="17" height="23" viewBox="0 0 17 23" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.5095 13.8399C16.5094 13.6906 16.5068 13.5583 16.4822 13.4085C16.4256 13.0625 16.3817 12.7146 16.3101 12.3712C16.1947 11.8342 16.0128 11.3214 15.7923 10.819C15.587 10.3514 15.3462 9.86875 15.01 9.48203C14.8839 9.33711 14.6543 9.29158 14.4755 9.34075C14.2618 9.39954 14.0426 9.63208 14.0722 9.87043C14.1297 10.3334 14.1487 10.7994 14.1139 11.265C14.1099 11.3177 14.1047 11.3704 14.0994 11.4231C14.0975 11.4407 14.0896 11.503 14.0864 11.5289C14.0714 11.6266 14.0539 11.7239 14.0338 11.8207C13.9891 12.0377 13.9282 12.2511 13.8557 12.4603C13.8384 12.5097 13.8203 12.5588 13.8014 12.6076C13.7934 12.6276 13.7853 12.6476 13.777 12.6676C13.782 12.6288 13.7871 12.5901 13.7918 12.5512C13.8859 11.7743 13.833 11.0006 13.7242 10.2285C13.5333 8.87339 13.0661 7.56572 12.4172 6.36374C11.7684 5.16182 10.9273 4.02822 9.94147 3.08169C8.92543 2.1061 7.68571 1.2863 6.33229 0.869677C5.79032 0.702832 5.2308 0.654223 4.66891 0.609301C4.19962 0.571777 3.92882 1.24172 4.28048 1.54705C4.55731 1.7874 4.84718 2.0123 5.12008 2.25728C5.24855 2.37273 5.37098 2.49474 5.48688 2.6228C5.54088 2.68237 5.59303 2.74358 5.64449 2.80535C5.67008 2.83601 5.69503 2.86724 5.71998 2.89844L5.7213 2.9001C5.73258 2.91504 5.74388 2.92998 5.75502 2.94504C5.95087 3.20806 6.12123 3.48948 6.27013 3.78147C6.30573 3.85132 6.33929 3.92219 6.37213 3.99338C6.388 4.02781 6.40323 4.06252 6.41844 4.09722C6.4204 4.10205 6.42267 4.10767 6.4255 4.11455C6.49017 4.27292 6.54684 4.43447 6.59864 4.59744C6.70092 4.91912 6.7766 5.24941 6.83109 5.58237C6.83466 5.6042 6.83771 5.62607 6.84095 5.64795C6.84344 5.66706 6.84945 5.71121 6.85109 5.72527C6.86063 5.80674 6.86776 5.8885 6.87404 5.97029C6.88739 6.14414 6.89149 6.3186 6.88951 6.4929C6.88771 6.65073 6.87782 6.80837 6.863 6.96548C6.85919 7.00442 6.85497 7.04332 6.85035 7.08218L6.84916 7.0927C6.83634 7.18191 6.82052 7.27065 6.80348 7.35913C6.7472 7.65137 6.665 7.93813 6.56447 8.21811C6.53571 7.99277 6.49606 7.76855 6.44393 7.54612C6.22899 6.62883 5.76056 5.79187 5.11703 5.10713C4.98221 4.9637 4.76886 4.91824 4.58256 4.96584C4.56163 4.97134 4.54064 4.97656 4.51959 4.98154C4.20363 5.05835 4.09235 5.36882 4.13592 5.65728C4.16471 5.84775 4.19239 6.03838 4.22128 6.22886C4.22289 6.23948 4.22816 6.27847 4.23151 6.30269C4.23441 6.32957 4.23707 6.35644 4.23961 6.38335C4.24869 6.47988 4.25255 6.57681 4.25426 6.67371C4.25692 6.82502 4.24781 6.97632 4.2318 7.12673C4.2296 7.14451 4.22714 7.16365 4.22572 7.17266C4.21929 7.21211 4.21223 7.25145 4.20455 7.29067C4.18717 7.38213 4.16495 7.47261 4.14095 7.5625C4.10011 7.71548 4.04762 7.86514 3.98907 8.01213C3.98654 8.01772 3.98341 8.02473 3.97941 8.03376C3.963 8.07075 3.94559 8.10728 3.92819 8.1438C3.88724 8.22971 3.84244 8.31372 3.79633 8.39695C3.61552 8.72312 3.39359 9.02759 3.17782 9.3311C2.73073 9.95991 2.25074 10.5658 1.81473 11.2024C1.4049 11.8008 1.07482 12.4554 0.849622 13.1453C0.737146 13.4899 0.666272 13.8439 0.610047 14.2014C0.573743 14.4323 0.533508 14.6579 0.533508 14.8914V15.4593C0.533508 15.683 0.5724 15.8922 0.611536 16.1134C0.675916 16.4774 0.740759 16.8383 0.853601 17.191C0.956775 17.5136 1.08602 17.8288 1.22989 18.1352C1.49181 18.6931 1.84176 19.2049 2.24279 19.6717C3.08553 20.6526 4.22064 21.3698 5.4139 21.8455C5.62355 21.9291 5.83688 22.0013 6.05289 22.0636H6.05206C8.74945 22.9104 10.6265 22.0595 10.6265 22.0595H10.6258C11.2538 21.8711 11.8594 21.5921 12.416 21.2483C13.5859 20.5257 14.5947 19.5071 15.3061 18.331C15.6104 17.8278 15.8529 17.2852 16.0451 16.73C16.151 16.4242 16.2298 16.1129 16.2893 15.7948C16.2981 15.7478 16.306 15.7004 16.3136 15.6527C16.3846 15.3293 16.4324 15.0508 16.4638 14.8132C16.4649 14.8092 16.5245 14.3733 16.5174 14.1532C16.52 14.0212 16.5158 13.9177 16.5095 13.8399ZM10.7401 16.1073C10.7362 16.2925 10.7221 16.4774 10.7012 16.6615L10.6992 16.6793L10.6962 16.7C10.6894 16.7462 10.6815 16.7923 10.6736 16.8383C10.655 16.9466 10.6317 17.054 10.6067 17.1609C10.5849 17.2537 10.5595 17.3455 10.5328 17.437C10.4992 17.5489 10.4611 17.6594 10.4186 17.7682C10.417 17.7686 10.3668 17.884 10.3533 17.9116C10.3203 17.9792 10.2842 18.0452 10.2467 18.1103C10.1971 18.1967 10.1422 18.2799 10.0839 18.3608C10.0808 18.3627 10.0119 18.4502 9.99186 18.4731C9.94611 18.5255 9.89762 18.5753 9.84806 18.624C9.80597 18.6654 9.76195 18.7047 9.71732 18.7433C9.69996 18.7583 9.68212 18.7727 9.66429 18.7872C9.56283 18.8621 9.45524 18.9284 9.34474 18.9891C9.29276 19.0176 9.23949 19.0437 9.18588 19.069C9.17369 19.0748 9.16129 19.0801 9.14901 19.0856C9.13976 19.0895 9.1307 19.0932 9.12411 19.0958C9.00045 19.1442 8.87323 19.1831 8.74476 19.2165C8.62321 19.2481 8.49965 19.2718 8.3756 19.2912C8.34718 19.2946 8.31874 19.2976 8.29027 19.3005C8.21241 19.3084 8.13426 19.3134 8.05614 19.3173C7.89821 19.3252 7.73988 19.3249 7.5819 19.3194C7.50182 19.3166 7.42186 19.3109 7.34203 19.3042C7.3024 19.3007 7.26281 19.2967 7.22328 19.2922L7.21107 19.2907C7.19845 19.2888 7.18583 19.2871 7.17325 19.2851C7.01798 19.2609 6.86456 19.226 6.71327 19.1838C6.6378 19.1627 6.56337 19.1381 6.48942 19.1123C6.45236 19.0993 6.41576 19.0852 6.37916 19.071C6.3755 19.0695 6.37185 19.068 6.3682 19.0666C6.35519 19.0608 6.34213 19.0551 6.32919 19.0491C6.18336 18.983 6.04357 18.9043 5.90848 18.8186C5.8514 18.7823 5.79618 18.7433 5.74152 18.7035C5.72577 18.6915 5.70345 18.6745 5.69498 18.6676C5.65653 18.6362 5.61947 18.6031 5.58263 18.5698C5.49161 18.4875 5.40778 18.3978 5.32914 18.3037C5.33529 18.3082 5.2525 18.1982 5.23502 18.1716C5.19147 18.1053 5.15253 18.0361 5.11559 17.966C5.10221 17.9406 5.05775 17.8395 5.04928 17.8228C5.03321 17.7792 5.01812 17.7352 5.00404 17.691C4.96495 17.569 4.9369 17.4437 4.91564 17.3175C4.92179 17.3484 4.90179 17.1891 4.89923 17.1536C4.89403 17.081 4.8923 17.0083 4.89159 16.9355C4.89086 16.8609 4.89347 16.7863 4.89703 16.7118C4.89884 16.6742 4.90165 16.6367 4.9045 16.5992C4.90573 16.583 4.91088 16.54 4.91405 16.5128C4.93358 16.3963 4.96129 16.2814 4.99696 16.1688C5.00541 16.142 5.04237 16.0494 5.04984 16.028C5.07222 15.9817 5.09606 15.9362 5.1213 15.8915C5.15187 15.837 5.186 15.7847 5.2212 15.7331C5.22604 15.7261 5.23109 15.7192 5.23605 15.7122C5.24613 15.6993 5.26019 15.6813 5.26847 15.6715C5.35685 15.5662 5.4567 15.4709 5.56132 15.382C5.57006 15.3746 5.57914 15.3676 5.58805 15.3603C5.60179 15.3498 5.62103 15.3352 5.63124 15.3278C5.68575 15.2884 5.74213 15.2517 5.79891 15.2157C5.92975 15.133 6.06783 15.0635 6.20414 14.9905C6.77455 14.6848 7.32328 14.2959 7.73483 13.7917C8.22587 13.1901 8.52731 12.4549 8.69518 11.7008C8.78759 11.2856 8.83646 10.8621 8.87032 10.4369C8.87408 10.4415 8.87787 10.446 8.8816 10.4506C8.87289 10.4403 8.92408 10.5048 8.9339 10.518C8.98385 10.5857 9.0328 10.6541 9.08075 10.7232C9.18414 10.8715 9.28177 11.0238 9.37574 11.1783C9.55475 11.4725 9.71422 11.7784 9.85856 12.091C9.88607 12.1509 9.9129 12.2111 9.93902 12.2716C9.9473 12.2911 9.96241 12.3266 9.967 12.3378C9.98259 12.376 9.99794 12.4142 10.013 12.4526C10.079 12.6189 10.1394 12.7874 10.1965 12.9568C10.3072 13.2851 10.4001 13.6193 10.4806 13.9562C10.5196 14.1195 10.5544 14.2838 10.587 14.4485C10.6052 14.5409 10.6222 14.6335 10.6381 14.7264C10.6461 14.7726 10.6535 14.8189 10.6609 14.8653C10.6634 14.8817 10.6771 14.9777 10.6801 14.9965C10.727 15.3647 10.7481 15.7361 10.7401 16.1073Z" fill="currentColor" />
        </svg>,
        num: groupData[2].unreadNum
      },
      {
        type: groupData[1].type,
        name: t('user.launch'),
        icon: <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.63037 0.351898H15.3696C15.5437 0.351898 15.7106 0.421039 15.8337 0.544109C15.9567 0.66718 16.0259 0.8341 16.0259 1.00815V14.5768C16.0256 14.6988 15.9913 14.8184 15.9269 14.922C15.8624 15.0257 15.7704 15.1093 15.661 15.1635L8.79268 18.5799C8.70177 18.6252 8.60158 18.6488 8.5 18.6488C8.39842 18.6488 8.29822 18.6252 8.20731 18.5799L1.339 15.1635C1.22964 15.1093 1.13756 15.0257 1.0731 14.922C1.00865 14.8184 0.974369 14.6988 0.974121 14.5768V1.00684C0.974121 0.832788 1.04326 0.665868 1.16633 0.542797C1.2894 0.419726 1.45632 0.350586 1.63037 0.350586V0.351898ZM8.31625 9.98565C8.37288 9.95576 8.43596 9.94014 8.5 9.94014C8.56404 9.94014 8.62711 9.95576 8.68375 9.98565L10.4307 10.9044C10.4958 10.9386 10.5692 10.9538 10.6425 10.9485C10.7158 10.9431 10.7862 10.9173 10.8456 10.874C10.905 10.8307 10.9511 10.7716 10.9787 10.7034C11.0063 10.6353 11.0142 10.5608 11.0016 10.4883L10.6682 8.54321C10.6573 8.48023 10.6619 8.41553 10.6815 8.3547C10.7012 8.29387 10.7354 8.23874 10.7811 8.19409L12.196 6.81596C12.2487 6.76463 12.286 6.69954 12.3037 6.62808C12.3213 6.55663 12.3185 6.48166 12.2957 6.41169C12.2729 6.34172 12.2309 6.27955 12.1745 6.23222C12.1182 6.1849 12.0497 6.15432 11.9768 6.14396L10.0238 5.86046C9.96062 5.85132 9.9006 5.82695 9.84893 5.78945C9.79725 5.75195 9.75547 5.70245 9.72718 5.64521L8.85306 3.87334C8.82054 3.80747 8.77024 3.75201 8.70785 3.71323C8.64545 3.67445 8.57346 3.6539 8.5 3.6539C8.42654 3.6539 8.35454 3.67445 8.29215 3.71323C8.22975 3.75201 8.17946 3.80747 8.14693 3.87334L7.27281 5.64521C7.24452 5.70245 7.20274 5.75195 7.15107 5.78945C7.09939 5.82695 7.03937 5.85132 6.97618 5.86046L5.02318 6.14396C4.95032 6.15432 4.88181 6.1849 4.82545 6.23222C4.76908 6.27955 4.7271 6.34172 4.70428 6.41169C4.68146 6.48166 4.6787 6.55663 4.69633 6.62808C4.71395 6.69954 4.75125 6.76463 4.804 6.81596L6.21887 8.19409C6.26461 8.23874 6.29879 8.29387 6.31846 8.3547C6.33813 8.41553 6.34269 8.48023 6.33175 8.54321L5.99837 10.4883C5.98607 10.5607 5.99424 10.635 6.02194 10.703C6.04964 10.7709 6.09577 10.8298 6.15513 10.8729C6.2145 10.916 6.28473 10.9417 6.35792 10.947C6.4311 10.9524 6.50431 10.9371 6.56931 10.9031L8.31625 9.98434V9.98565Z" fill="currentColor" />
        </svg>,
        num: groupData[1].unreadNum
      },
      {
        type: groupData[0].type,
        name: t('user.system'),
        icon: <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.94983 0.70804C9.69319 0.587337 9.39026 0.627181 9.17229 0.808236L4.96936 4.30628L1.61663 4.29281C1.42385 4.29281 1.23811 4.36898 1.10159 4.5055C0.963892 4.64202 0.885962 4.82777 0.885962 5.0223L0.870728 10.8682C0.870728 11.0627 0.946899 11.2491 1.08401 11.3862C1.22053 11.5244 1.40686 11.6012 1.60139 11.6012H4.99104L9.17287 15.0864C9.30647 15.1977 9.47346 15.2551 9.64045 15.2551C9.74592 15.2551 9.85139 15.2323 9.95041 15.186C10.2071 15.0659 10.3711 14.808 10.3711 14.5244V1.36956C10.3711 1.08656 10.2071 0.828744 9.94983 0.70804ZM12.7834 4.36195C12.4231 4.19144 11.9947 4.34671 11.8196 4.70531C11.6449 5.06507 11.7961 5.50042 12.1535 5.68031C12.1998 5.70433 13.2944 6.27328 13.2944 7.89339C13.2944 9.62425 12.2707 10.1979 12.2367 10.2166C11.8758 10.3971 11.7293 10.8366 11.9098 11.1975C12.0387 11.4541 12.2959 11.6018 12.5649 11.6018C12.6744 11.6018 12.7864 11.5772 12.8906 11.525C12.9668 11.4864 14.7557 10.5606 14.7557 7.89339C14.7557 5.31995 12.8637 4.40003 12.7834 4.36195ZM15.0586 2.21566C14.7217 1.99945 14.2746 2.09788 14.0543 2.43421C13.8352 2.77054 13.9313 3.22288 14.2664 3.44437C14.3455 3.49652 16.2176 4.7721 16.2176 7.9473C16.2176 11.1295 14.4217 12.4063 14.3502 12.4549C14.0139 12.6793 13.9237 13.1323 14.1475 13.4686C14.2881 13.6795 14.5201 13.7938 14.7563 13.7938C14.8951 13.7938 15.0364 13.7534 15.1612 13.6707C15.2637 13.6028 17.6789 11.9469 17.6789 7.94671C17.6789 3.9389 15.1658 2.28421 15.0586 2.21566Z" fill="currentColor" />
        </svg>,
        num: groupData[0]?.unreadNum || 0
      }
    ], [groupData]);


  const itemClick = (item) => {
    setNavActive(item.type);
    setTabViewedInStorage(item.type);
    setTabReadMap((prev) => ({ ...prev, [String(item.type)]: true }));
  };

  const openUrl = (item) => {
    updateGroupData(item);
    if (item.webClickUrl) {
      window.open(item.webClickUrl, '_blank')
    }
  }
  const readAll = async () => {
    await getUserLetterRead({
      errorPop: true,
    }).then(() => {
      message.success(t('notify.readAll'));
      const oldData = notificationData;
      const newData = oldData.map(m => {
        m.isRead = true;
        return m;
      });
      setNotificationData(newData);
    })
    getGroupData(false)
  };


  const singleRead = async (item, ids = "") => {
    if (ids) {
      await getUserLetterSingleRead({
        errorPop: true,
      }, ids);
      getGroupData(false);
    } else {
      if (!item.isRead) {
        await getUserLetterSingleRead({
          errorPop: true,
        }, item.id);
        getGroupData(false);
      }
    }
  }

  const updateGroupData = async (item) => {
    await singleRead(item);
    const oldData = notificationData;
    const newData = oldData.map(m => {
      if (m.id == item.id) {
        m.isRead = true;
      }
      return m;
    });
    setNotificationData(newData);
  }
  const viewMore = () => {
    const since = notificationData[notificationData.length - 1].id;
    getListData(since);
  }
  const goSetting = () => {
    location.href = getUrl("/accounts/user/notification-setting");
  };

  const goBack = () => {
    router.back();
  };

  return (
    <Layout isShowNav={false} isShowFoot={false} useBottomBar={false}>
      <div className={cx(styles.notification, className)}>
        {/* PC端头部 */}
        {!isMobile && (
          <div className={styles.head}>
            <h2>{t('user.notification')}</h2>
            <div className={styles.headRight}>
              <p onClick={readAll}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_15825_12413)">
                    <rect x="1" y="7" width="18" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
                    <path
                      d="M2.12083 17.8009L3.5835 10H16.521L18.4565 17.7419C18.6162 18.381 18.1329 19 17.4742 19H3.11598C2.48202 19 2.00399 18.424 2.12083 17.8009Z"
                      stroke="currentColor" strokeWidth="1.8" />
                    <path d="M11.5 7V2.5C11.5 1.67157 10.8284 1 10 1V1C9.17157 1 8.5 1.67157 8.5 2.5V7"
                      stroke="currentColor" strokeWidth="1.8" />
                    <path
                      d="M7.00217 14.5716C7.07247 14.0796 6.73056 13.6237 6.2385 13.5534C5.74644 13.4831 5.29056 13.825 5.22027 14.3171L7.00217 14.5716ZM5.22027 14.3171L4.66471 18.2059L6.44662 18.4605L7.00217 14.5716L5.22027 14.3171Z"
                      fill="currentColor" />
                  </g>
                  <defs>
                    <clipPath id="clip0_15825_12413">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg> {t('user.readAll')}</p>
              <p onClick={goSetting}>
                <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M17.1967 4.26772L11.5553 1.07529C11.0786 0.807437 10.5409 0.666748 9.99414 0.666748C9.44733 0.666748 8.90972 0.807437 8.43301 1.07529L2.79295 4.26772C2.32165 4.53205 1.92878 4.91652 1.65433 5.38201C1.37987 5.84749 1.23363 6.37737 1.23047 6.91773V13.2999C1.23047 14.3915 1.82956 15.4048 2.79025 15.9499L8.43301 19.1396C8.90951 19.4081 9.4472 19.5492 9.99414 19.5492C10.5411 19.5492 11.0788 19.4081 11.5553 19.1396L17.1953 15.9499C17.6664 15.6854 18.059 15.3009 18.3332 14.8354C18.6074 14.3699 18.7534 13.8401 18.7565 13.2999V6.91773C18.7533 6.37736 18.6072 5.84746 18.333 5.3818C18.0588 4.91614 17.6663 4.53131 17.1953 4.26637L17.1967 4.26772ZM17.4261 13.2999C17.4247 13.6102 17.341 13.9145 17.1836 14.1819C17.0262 14.4493 16.8007 14.6701 16.5301 14.8219L10.8901 18.0116C10.3369 18.322 9.65547 18.322 9.10091 18.0116L3.45815 14.8219C3.18737 14.6703 2.96172 14.4495 2.80429 14.1821C2.64687 13.9147 2.56333 13.6102 2.56222 13.2999V6.91773C2.56222 6.29436 2.90494 5.71011 3.45815 5.39843L9.09956 2.20734C9.37275 2.05317 9.68112 1.97216 9.99481 1.97216C10.3085 1.97216 10.6169 2.05317 10.8901 2.20734L16.5301 5.39843C17.0847 5.71011 17.4274 6.29301 17.4274 6.91908V13.2999H17.4261Z"
                    fill="currentColor" stroke="currentColor" strokeWidth="0.5" />
                  <path
                    d="M10.0088 6.57386C8.01587 6.57521 6.40076 8.15793 6.39941 10.1104C6.39941 12.0641 8.01587 13.6482 10.0088 13.6496C12.003 13.6496 13.6195 12.0641 13.6208 10.1104C13.6195 8.15658 12.0017 6.57251 10.0088 6.57251V6.57386ZM10.0088 12.3461C8.75124 12.3448 7.73252 11.345 7.72847 10.1104C7.72847 8.87846 8.75393 7.87593 10.0088 7.87593C11.2677 7.87593 12.2891 8.87846 12.2891 10.1104C12.2891 11.3436 11.2677 12.3448 10.0088 12.3448V12.3461Z"
                    fill="currentColor" stroke="currentColor" strokeWidth="0.5" />
                </svg> {t('notice.setting')}</p>
            </div>
          </div>
        )}
        {/* 移动端头部 */}
        {isMobile && (
          <div className={styles.headMobile}>
            <div className={styles.headLeft} onClick={goBack}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.headTitle}>{t('user.notification')}</div>
            <div className={styles.headRightMobile}>
              <div className={styles.headIcon} onClick={goSetting}>
                <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M17.1967 4.26772L11.5553 1.07529C11.0786 0.807437 10.5409 0.666748 9.99414 0.666748C9.44733 0.666748 8.90972 0.807437 8.43301 1.07529L2.79295 4.26772C2.32165 4.53205 1.92878 4.91652 1.65433 5.38201C1.37987 5.84749 1.23363 6.37737 1.23047 6.91773V13.2999C1.23047 14.3915 1.82956 15.4048 2.79025 15.9499L8.43301 19.1396C8.90951 19.4081 9.4472 19.5492 9.99414 19.5492C10.5411 19.5492 11.0788 19.4081 11.5553 19.1396L17.1953 15.9499C17.6664 15.6854 18.059 15.3009 18.3332 14.8354C18.6074 14.3699 18.7534 13.8401 18.7565 13.2999V6.91773C18.7533 6.37736 18.6072 5.84746 18.333 5.3818C18.0588 4.91614 17.6663 4.53131 17.1953 4.26637L17.1967 4.26772ZM17.4261 13.2999C17.4247 13.6102 17.341 13.9145 17.1836 14.1819C17.0262 14.4493 16.8007 14.6701 16.5301 14.8219L10.8901 18.0116C10.3369 18.322 9.65547 18.322 9.10091 18.0116L3.45815 14.8219C3.18737 14.6703 2.96172 14.4495 2.80429 14.1821C2.64687 13.9147 2.56333 13.6102 2.56222 13.2999V6.91773C2.56222 6.29436 2.90494 5.71011 3.45815 5.39843L9.09956 2.20734C9.37275 2.05317 9.68112 1.97216 9.99481 1.97216C10.3085 1.97216 10.6169 2.05317 10.8901 2.20734L16.5301 5.39843C17.0847 5.71011 17.4274 6.29301 17.4274 6.91908V13.2999H17.4261Z"
                    fill="currentColor" stroke="currentColor" strokeWidth="0.5" />
                  <path
                    d="M10.0088 6.57386C8.01587 6.57521 6.40076 8.15793 6.39941 10.1104C6.39941 12.0641 8.01587 13.6482 10.0088 13.6496C12.003 13.6496 13.6195 12.0641 13.6208 10.1104C13.6195 8.15658 12.0017 6.57251 10.0088 6.57251V6.57386ZM10.0088 12.3461C8.75124 12.3448 7.73252 11.345 7.72847 10.1104C7.72847 8.87846 8.75393 7.87593 10.0088 7.87593C11.2677 7.87593 12.2891 8.87846 12.2891 10.1104C12.2891 11.3436 11.2677 12.3448 10.0088 12.3448V12.3461Z"
                    fill="currentColor" stroke="currentColor" strokeWidth="0.5" />
                </svg>
              </div>
            </div>
          </div>
        )}
        <div className={styles.container}>
          {/* PC端导航 */}
          {!isMobile && (
            <div className={styles.nav}>
              {navData.map(item => (
                <div className={cx(styles.item, navActive == item.type && styles.active)} key={item.type} onClick={() => itemClick(item)}>
                  <span>
                    {item.icon}
                    {item.name}
                  </span>
                  {!!item.num && !tabReadMap[String(item.type)] && <div>{item.num}</div>}
                </div>
              ))}
            </div>
          )}
          {/* 移动端Tab导航 */}
          {isMobile && (
            <div className={styles.navMobile}>
              <div className={styles.navContainer}>
                {navData.map(item => (
                  <div 
                    className={cx(styles.tabItem, navActive == item.type && styles.tabActive)} 
                    key={item.type} 
                    onClick={() => itemClick(item)}
                  >
                    {item.name}
                    {item.num > 0 && !tabReadMap[String(item.type)] && <span className={styles.tabUnreadDot} />}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={styles.content}>

            <div className={styles.noteContent}>
              {
                !notificationData.length && (
                  <div className={styles.noData}>
                    <img src={Nodata} />
                    <p>
                      {t('home.noData')}
                    </p>
                  </div>
                )
              }
              {/* PC端通知列表 */}
              {!isMobile && notificationData.map((item, i) => (
                <div className={cx(styles.noteItem, item.isRead && styles.isRead)} key={i}  >
                  <div className={cx(styles.noteLeft, item.hotActivityImageUrl && styles.active)}>
                    <div className={styles.title}>
                      <div >{!item.isRead && <i></i>}
                        {item.title}
                      </div>
                    </div>
                    {navActive != 1 ? (<p >{item.content}</p>) : (<>
                      <p >{item.content}</p>{item.webClickUrl && <div className={styles.open}><span onClick={(e) => {
                        e.stopPropagation();
                        openUrl(item)
                      }}>{t('noticition.ViewDetails')}</span></div>}
                    </>)}
                    {item.webClickUrl && navActive != 1 && <div className={styles.open}><span onClick={(e) => {
                      e.stopPropagation();
                      openUrl(item)
                    }}>{t('noticition.ViewDetails')}</span></div>}
                    <p className={styles.time}>{dayjs(item.createTime).format('YYYY-MM-DD  HH:mm:ss')}</p>
                  </div>
                  {item.hotActivityImageUrl && <img src={item.hotActivityImageUrl} alt="" />}
                </div>
              ))}
              {/* 移动端通知列表 */}
              {isMobile && notificationData.map((item, i) => (
                <div 
                  className={cx(styles.noteItemMobile, item.isRead && styles.isReadMobile)} 
                  key={i}
                  onClick={() => {
                    updateGroupData(item);
                  }}
                >
                  <div className={styles.noteTitleMobile}>{item.title}</div>
                  <div className={styles.noteContentMobile}>{item.content}</div>
                  <div className={styles.noteTimeMobile}>{dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss')}</div>
                </div>
              ))}

              {isMore && (
                <div className={styles.more} onClick={viewMore}>
                  {t('cmpt.viewmore')}
                  <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '3px' }}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.3 3L7 3.6L3.5 6.6L0 3.6L0.7 3L3.5 5.4L6.3 3Z" fill="currentColor" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.3 0L7 0.6L3.5 3.6L0 0.6L0.7 0L3.5 2.4L6.3 0Z" fill="currentColor" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
        {loading && <XtLoading />}
      </div>
    </Layout>
  );
};
export default observer(NotificationCenter);